/**
 * Catálogos de alto riesgo de la entidad de Actividad Vulnerable (zonas, actos/productos y
 * actividades económicas). Viven en `pld_config` y se congelan como snapshot en cada versión
 * de la metodología. Endpoints existentes: GET/PUT `/api/pld/gafi/factores-config`.
 * Son controles OPERATIVOS: siguen editables aunque la metodología esté sellada.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Save, Loader2, X } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ESTADOS_MX, ACTIVIDADES_ECONOMICAS } from "@/lib/catalogos_maestros";
import { useMiActividad } from "@/hooks/useMiActividad";

const ROLES_EDITAN = ["admin_entidad", "oficial_cumplimiento", "cumplimiento_inmo"];

function Chips({ titulo, ayuda, testid, items, opciones, onAdd, onRemove, disabled }) {
  const [valor, setValor] = useState("");
  const agregar = () => {
    const v = String(valor || "").trim();
    if (!v) return;
    onAdd(v);
    setValor("");
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid={testid}>
      <p className="text-sm font-medium text-slate-800">{titulo}</p>
      <p className="mb-2 text-xs text-slate-500">{ayuda}</p>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {items.length === 0 && <span className="text-xs italic text-slate-400">Sin elementos</span>}
        {items.map((it) => (
          <Badge key={it} variant="outline" className="max-w-full whitespace-normal break-words text-left leading-tight"
            data-testid={`${testid}-item-${it}`}>
            {it}
            {!disabled && (
              <button type="button" className="ml-1.5 text-slate-400 hover:text-red-600"
                data-testid={`${testid}-quitar-${it}`} onClick={() => onRemove(it)}>
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <div className="flex flex-col gap-2 sm:flex-row">
          {opciones ? (
            <select className="h-8 w-full min-w-0 flex-1 rounded border border-slate-300 px-2 text-sm"
              data-testid={`${testid}-select`} value={valor} onChange={(e) => setValor(e.target.value)}>
              <option value="">Selecciona…</option>
              {opciones.filter((o) => !items.includes(o)).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input className="h-8 flex-1" data-testid={`${testid}-input`} value={valor}
              placeholder="Escribe y agrega" onChange={(e) => setValor(e.target.value)} />
          )}
          <Button size="sm" variant="outline" data-testid={`${testid}-add`} onClick={agregar}>Agregar</Button>
        </div>
      )}
    </div>
  );
}

export function AvEbrCatalogos() {
  const { user } = useAuth();
  const act = useMiActividad();
  const puedeEditar = ROLES_EDITAN.includes(user?.role);
  const [cfg, setCfg] = useState({ zonas_alto_riesgo: [], productos_alto_riesgo: [], actividades_alto_riesgo: [] });
  const [meta, setMeta] = useState({ por: null, fecha: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const c = await api.get("/pld/gafi/factores-config").then((r) => r.data);
      setCfg({
        zonas_alto_riesgo: c.zonas_alto_riesgo || [],
        productos_alto_riesgo: c.productos_alto_riesgo || [],
        actividades_alto_riesgo: c.actividades_alto_riesgo || [],
      });
      setMeta({ por: c.catalogos_guardado_por || null, fecha: c.catalogos_guardado_fecha || null });
    } catch { /* sin acceso al catálogo: el bloque queda vacío en lectura */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = (k, v) => setCfg((p) => ({ ...p, [k]: p[k].includes(v) ? p[k] : [...p[k], v] }));
  const rm = (k, v) => setCfg((p) => ({ ...p, [k]: p[k].filter((x) => x !== v) }));

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put("/pld/gafi/factores-config", cfg);
      toast.success("Catálogos de alto riesgo guardados.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="av-ebr-catalogos">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Tag className="h-4 w-4 text-primary" /> Catálogos de alto riesgo de la entidad
        </CardTitle>
        {puedeEditar && (
          <Button size="sm" onClick={guardar} disabled={saving} data-testid="av-ebr-catalogos-guardar"
            className="bg-primary hover:bg-sidebar-hover">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Guardar catálogos
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <Chips titulo="Zonas o plazas de alto riesgo" testid="av-cat-zonas"
          ayuda="Alimentan el factor Zona geográfica del EBR."
          items={cfg.zonas_alto_riesgo} opciones={ESTADOS_MX} disabled={!puedeEditar}
          onAdd={(v) => add("zonas_alto_riesgo", v)} onRemove={(v) => rm("zonas_alto_riesgo", v)} />
        <Chips titulo={`Actos u operaciones de alto riesgo${act.nombreCorto ? ` · ${act.nombreCorto}` : ""}`}
          testid="av-cat-productos"
          ayuda="Alimentan el factor Producto/servicio del EBR (por ejemplo, operaciones liquidadas en efectivo o con pago de terceros)."
          items={cfg.productos_alto_riesgo} disabled={!puedeEditar}
          onAdd={(v) => add("productos_alto_riesgo", v)} onRemove={(v) => rm("productos_alto_riesgo", v)} />
        <Chips titulo="Actividades económicas de alto riesgo del cliente" testid="av-cat-actividades"
          ayuda="Alimentan el factor Cliente/usuario del EBR."
          items={cfg.actividades_alto_riesgo} opciones={ACTIVIDADES_ECONOMICAS.map((a) => a.value)} disabled={!puedeEditar}
          onAdd={(v) => add("actividades_alto_riesgo", v)} onRemove={(v) => rm("actividades_alto_riesgo", v)} />
        {meta.fecha && (
          <p className="text-xs text-slate-500" data-testid="av-ebr-catalogos-evidencia">
            Último guardado: {meta.fecha}{meta.por ? ` · ${meta.por}` : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
